import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, 
  Bot, 
  Send, 
  Edit, 
  Star, 
  Target, 
  BarChart3, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Eye,
  Trash2,
  Copy,
  RefreshCw,
  Zap,
  Brain,
  TrendingUp,
  MessageSquare
} from 'lucide-react';
import { Button } from '../../../components/ui/Button/Button';
import { cn } from '../../../utils/cn';

interface GeneratedResponse {
  id: string;
  content: string;
  confidence: number;
  strategy: string;
  tone: string;
  estimatedPerformance: {
    clickRate: number;
    responseRate: number;
    conversionRate: number;
  };
  complianceChecks: {
    passed: boolean;
    issues: string[];
  };
  alternatives: string[];
  metadata: {
    generatedAt: string;
    processingTime: number;
    model: string;
    traceId: string;
  };
}

interface EnhancedConversation {
  id: string;
  conversationId: string;
  contact: {
    name: string;
    email: string;
    company?: string;
  };
  aiAnalysis?: {
    sentiment: string;
    intent: string;
    confidence: number;
    entities: any[];
    businessContext: any;
    riskFactors: string[];
  };
  suggestedResponses?: any[];
}

interface AIResponsePanelProps {
  conversation: EnhancedConversation;
  onResponseSelect: (response: GeneratedResponse) => void;
  onResponseEdit: (response: GeneratedResponse) => void;
  onResponseSend: (responseId: string, customText?: string) => void;
}

export const AIResponsePanel: React.FC<AIResponsePanelProps> = ({
  conversation,
  onResponseSelect,
  onResponseEdit,
  onResponseSend
}) => {
  const [generatedResponses, setGeneratedResponses] = useState<GeneratedResponse[]>([]);
  const [selectedResponse, setSelectedResponse] = useState<GeneratedResponse | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [generationPreferences, setGenerationPreferences] = useState({
    strategy: 'adaptive',
    tone: 'professional',
    includePersonalization: true,
    responseLength: 'medium',
    includeQuestions: false,
    urgency: 'normal'
  });

  // Mock generated responses (in real app, these would come from API)
  useEffect(() => {
    if (conversation.suggestedResponses && conversation.suggestedResponses.length > 0) {
      const mockResponses: GeneratedResponse[] = conversation.suggestedResponses.map((response, index) => ({
        id: `response-${index}`,
        content: typeof response === 'string' ? response : response.text || response.content || 'Thank you for your message. We appreciate your interest.',
        confidence: typeof response === 'object' && response.confidence ? response.confidence : 0.85 - (index * 0.1),
        strategy: typeof response === 'object' && response.strategy ? response.strategy : ['consultative', 'direct', 'educational'][index] || 'consultative',
        tone: generationPreferences.tone,
        estimatedPerformance: {
          clickRate: Math.round((75 + Math.random() * 20) * 100) / 100,
          responseRate: Math.round((45 + Math.random() * 30) * 100) / 100,
          conversionRate: Math.round((15 + Math.random() * 20) * 100) / 100
        },
        complianceChecks: {
          passed: true,
          issues: []
        },
        alternatives: [
          'Alternative phrasing option 1',
          'Alternative phrasing option 2'
        ],
        metadata: {
          generatedAt: new Date().toISOString(),
          processingTime: 1500 + Math.random() * 1000,
          model: 'Gemini 2.0 Flash',
          traceId: `trace-${Date.now()}-${index}`
        }
      }));
      
      setGeneratedResponses(mockResponses);
      if (mockResponses.length > 0) {
        setSelectedResponse(mockResponses[0]);
      }
    }
  }, [conversation.suggestedResponses, generationPreferences.tone]);

  const handleGenerateResponse = async () => {
    setIsGenerating(true);
    
    try {
      // In real app, this would call the API
      const response = await fetch(`/api/conversations/management/${conversation.id}/generate-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preferences: generationPreferences,
          context: conversation.aiAnalysis
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data.responses) {
          const newResponses = result.data.responses.map((r: any, index: number) => ({
            id: r.id || `response-${Date.now()}-${index}`,
            content: r.content,
            confidence: r.confidence,
            strategy: r.strategy,
            tone: generationPreferences.tone,
            estimatedPerformance: r.estimatedPerformance || {
              clickRate: Math.round((75 + Math.random() * 20) * 100) / 100,
              responseRate: Math.round((45 + Math.random() * 30) * 100) / 100,
              conversionRate: Math.round((15 + Math.random() * 20) * 100) / 100
            },
            complianceChecks: r.complianceChecks || { passed: true, issues: [] },
            alternatives: r.alternatives || [],
            metadata: r.metadata || {
              generatedAt: new Date().toISOString(),
              processingTime: 1500,
              model: 'Gemini 2.0 Flash',
              traceId: result.data.traceId
            }
          }));
          
          setGeneratedResponses(newResponses);
          if (newResponses.length > 0) {
            setSelectedResponse(newResponses[0]);
          }
        }
      }
    } catch (error) {
      console.error('Error generating response:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleResponseSelect = (response: GeneratedResponse) => {
    setSelectedResponse(response);
    setEditedContent(response.content);
    onResponseSelect(response);
  };

  const handleEditResponse = () => {
    setEditMode(true);
    setEditedContent(selectedResponse?.content || '');
  };

  const handleSaveEdit = () => {
    if (selectedResponse) {
      const updatedResponse = { ...selectedResponse, content: editedContent };
      setSelectedResponse(updatedResponse);
      setGeneratedResponses(prev => 
        prev.map(r => r.id === selectedResponse.id ? updatedResponse : r)
      );
      onResponseEdit(updatedResponse);
    }
    setEditMode(false);
  };

  const handleSendResponse = () => {
    if (selectedResponse) {
      onResponseSend(selectedResponse.id, editMode ? editedContent : selectedResponse.content);
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-400 bg-green-500/20 border-green-500/30';
    if (confidence >= 0.6) return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
    return 'text-red-400 bg-red-500/20 border-red-500/30';
  };

  const getStrategyIcon = (strategy: string) => {
    switch (strategy) {
      case 'consultative': return Target;
      case 'direct': return Zap;
      case 'educational': return Brain;
      default: return MessageSquare;
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-neutral-700/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center">
            <Sparkles className="w-5 h-5 mr-2 text-cyan-400" />
            AI Response Generator
          </h3>
          <Button
            onClick={handleGenerateResponse}
            disabled={isGenerating}
            size="sm"
            className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
          >
            {isGenerating ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            {isGenerating ? 'Generating...' : 'Generate'}
          </Button>
        </div>

        {/* Generation Preferences */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-neutral-400 text-xs mb-1 block">Strategy</label>
            <select
              value={generationPreferences.strategy}
              onChange={(e) => setGenerationPreferences(prev => ({ ...prev, strategy: e.target.value }))}
              className="w-full px-2 py-1 bg-neutral-800/50 border border-neutral-700 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="adaptive">Adaptive</option>
              <option value="consultative">Consultative</option>
              <option value="direct">Direct</option>
              <option value="educational">Educational</option>
            </select>
          </div>
          <div>
            <label className="text-neutral-400 text-xs mb-1 block">Tone</label>
            <select
              value={generationPreferences.tone}
              onChange={(e) => setGenerationPreferences(prev => ({ ...prev, tone: e.target.value }))}
              className="w-full px-2 py-1 bg-neutral-800/50 border border-neutral-700 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="professional">Professional</option>
              <option value="friendly">Friendly</option>
              <option value="casual">Casual</option>
              <option value="formal">Formal</option>
            </select>
          </div>
        </div>
      </div>

      {/* Response Generation Status */}
      {isGenerating && (
        <div className="px-6 py-4 bg-cyan-500/10 border-b border-cyan-500/20">
          <div className="flex items-center space-x-3">
            <div className="w-4 h-4 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin"></div>
            <div>
              <p className="text-cyan-400 text-sm font-medium">AI is analyzing and generating responses...</p>
              <p className="text-neutral-400 text-xs">Using {conversation.aiAnalysis ? 'conversation context and sentiment analysis' : 'basic content analysis'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Generated Response Options */}
      <div className="flex-1 overflow-y-auto p-6">
        {generatedResponses.length === 0 ? (
          <div className="text-center py-8">
            <Bot className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No responses generated yet</h3>
            <p className="text-neutral-400 text-sm mb-4">
              Click "Generate" to create AI-powered responses tailored to this conversation
            </p>
            <Button
              onClick={handleGenerateResponse}
              disabled={isGenerating}
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Generate AI Responses
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Response Options Grid */}
            <div className="grid gap-3">
              {generatedResponses.map((response, index) => {
                const StrategyIcon = getStrategyIcon(response.strategy);
                
                return (
                  <motion.div
                    key={response.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => handleResponseSelect(response)}
                    className={cn(
                      "p-4 border rounded-xl cursor-pointer transition-all duration-200 hover:border-blue-500/50",
                      selectedResponse?.id === response.id
                        ? "border-blue-500/50 bg-blue-500/10"
                        : "border-neutral-700/50 bg-neutral-800/30 hover:bg-neutral-800/50"
                    )}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <StrategyIcon className="w-4 h-4 text-cyan-400" />
                        <span className="text-neutral-300 text-sm capitalize">{response.strategy}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={cn(
                          "px-2 py-1 rounded-full text-xs font-medium border",
                          getConfidenceColor(response.confidence)
                        )}>
                          {Math.round(response.confidence * 100)}%
                        </span>
                        {response.complianceChecks.passed ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-red-400" />
                        )}
                      </div>
                    </div>
                    
                    <p className="text-white text-sm mb-3 line-clamp-3">
                      {response.content}
                    </p>
                    
                    <div className="flex items-center justify-between text-xs text-neutral-400">
                      <span className="flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {Math.round(response.metadata.processingTime)}ms
                      </span>
                      <span className="flex items-center">
                        <TrendingUp className="w-3 h-3 mr-1" />
                        {response.estimatedPerformance.responseRate}% response rate
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Selected Response Editor */}
            {selectedResponse && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-neutral-700/50 rounded-xl p-6 bg-neutral-800/20"
              >
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-white font-medium flex items-center">
                    <Edit className="w-4 h-4 mr-2 text-blue-400" />
                    Selected Response
                  </h4>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleEditResponse}
                      className="border-neutral-600 text-neutral-300"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigator.clipboard.writeText(selectedResponse.content)}
                      className="border-neutral-600 text-neutral-300"
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Copy
                    </Button>
                  </div>
                </div>

                {editMode ? (
                  <div className="space-y-4">
                    <textarea
                      value={editedContent}
                      onChange={(e) => setEditedContent(e.target.value)}
                      className="w-full h-32 px-3 py-2 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent resize-none"
                      placeholder="Edit your response..."
                    />
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditMode(false)}
                        className="border-neutral-600 text-neutral-300"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveEdit}
                        className="bg-blue-500 hover:bg-blue-600"
                      >
                        Save Changes
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-neutral-900/50 rounded-lg border border-neutral-700/30">
                      <p className="text-white text-sm leading-relaxed">
                        {selectedResponse.content}
                      </p>
                    </div>

                    {/* Performance Predictions */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-400">
                          {selectedResponse.estimatedPerformance.clickRate}%
                        </p>
                        <p className="text-neutral-400 text-xs">Click Rate</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-400">
                          {selectedResponse.estimatedPerformance.responseRate}%
                        </p>
                        <p className="text-neutral-400 text-xs">Response Rate</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-purple-400">
                          {selectedResponse.estimatedPerformance.conversionRate}%
                        </p>
                        <p className="text-neutral-400 text-xs">Conversion Rate</p>
                      </div>
                    </div>

                    {/* Send Actions */}
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-neutral-600 text-neutral-300"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Preview
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSendResponse}
                        className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                      >
                        <Send className="w-3 h-3 mr-1" />
                        Send Response
                      </Button>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}; 